import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Fab, Drawer, useTheme, useMediaQuery, Badge, Tooltip, Chip, Divider, Button } from '@mui/material';
import { Menu, Notifications, Chat as ChatIcon, Close as CloseIcon, SmartToy, Minimize, OpenInFull, Visibility, PanTool, Schedule, FiberManualRecord } from '@mui/icons-material';
import BottomNav from './BottomNav';
import { useAuth } from '../../context/AuthContext';
import Chatbot from '../Chatbot';
import AIChatPanel from '../AIChatPanel';
import api from '../../api/axios';
import { useChatDrawer } from '../../context/ChatDrawerContext';
import SyncModal from '../sync/SyncModal';

const AppLayout = ({ children, title = 'Soil Testing' }) => {
  const { user, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role || 'manager';
  const { chatOpen, setChatOpen } = useChatDrawer();
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // AI activity badge count
  const [aiBadge, setAiBadge] = useState(0);
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await api.get('/ai/activity');
        const s = res.data?.summary || {};
        setAiBadge((s.pending_count || 0) + (s.alerts_count || 0));
      } catch { /* silent */ }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={1} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          {role !== 'worker' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSyncModalOpen(true)}
              sx={{ mr: 2, borderRadius: 2 }}
            >
              Sync Data
            </Button>
          )}
          <IconButton color="inherit">
            <Notifications />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <SyncModal open={syncModalOpen} onClose={() => setSyncModalOpen(false)} />

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          pt: 8, 
          pb: 10,
          px: 2,
          bgcolor: '#F5F5F5',
          minHeight: '100vh'
        }}
      >
        {children}
      </Box>
      
      {/* AI Assistant Floating Action Button */}
      {!chatOpen && (
        <Fab 
          aria-label="AI Assistant" 
          onClick={() => setChatOpen(true)}
          sx={{ 
            position: 'fixed', 
            bottom: { xs: 70, sm: 32 }, 
            right: { xs: 16, sm: 32 },
            zIndex: 1000,
            background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(30, 41, 59, 0.5)',
            '&:hover': { background: 'linear-gradient(135deg, #334155 0%, #475569 100%)' }
          }}
        >
          <Badge badgeContent={aiBadge} color="error" max={9}>
            <SmartToy />
          </Badge>
        </Fab>
      )}

      {/* Persistent AI Chat Drawer */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        variant={isMobile ? 'temporary' : 'persistent'}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            height: { xs: '90vh', sm: '100%' },
            borderTopLeftRadius: { xs: 16, sm: 0 },
            borderTopRightRadius: { xs: 16, sm: 0 },
            overflow: 'hidden',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Branded Header */}
            <Box sx={{
              px: 2, py: 1.5,
              background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
              color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Badge variant="dot" color="success" overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                  <SmartToy sx={{ fontSize: 26 }} />
                </Badge>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2, fontSize: '0.95rem' }}>
                    AI Assistant
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                    Always listening &bull; Ask me anything
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Minimize">
                  <IconButton size="small" sx={{ color: 'white' }} onClick={() => setChatOpen(false)}>
                    <Minimize fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton size="small" sx={{ color: 'white' }} onClick={() => setChatOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Chat Content */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {role === 'manager' || role === 'admin' ? (
                    <AIChatPanel embedded={true} />
                ) : (
                    <Chatbot embedded={true} />
                )}
            </Box>
        </Box>
      </Drawer>

      <BottomNav role={role} />
    </Box>
  );
};

export default AppLayout;
